import { z } from 'zod';
import { CapacityMetricSchema, ModuleKeySchema } from './commercial.js';
/**
 * Entitlement Tier 1: Contracted Entitlement (What the contract legally grants).
 */
export const ContractedEntitlementSchema = z.object({
    organisationId: z.string(),
    contractId: z.string(),
    modules: z.array(ModuleKeySchema),
    capacity: z.record(CapacityMetricSchema, z.number().int().nonnegative()),
    validFrom: z.iso.datetime(),
    validUntil: z.iso.datetime(),
});
/**
 * Entitlement Tier 2: Effective Software Entitlement (Active granted state with waivers/overrides/grace).
 */
export const EffectiveEntitlementSchema = z.object({
    organisationId: z.string(),
    contractId: z.string().nullable(),
    modules: z.array(ModuleKeySchema),
    capacity: z.record(CapacityMetricSchema, z.number().int().nonnegative()),
    overrides: z.array(z.object({
        id: z.string(),
        featureOrMetric: z.string(),
        grantType: z.enum(['module_grant', 'capacity_boost', 'waiver', 'grace_extension']),
        value: z.unknown(),
        reason: z.string(),
        grantedByUserId: z.string(),
        grantedByDisplayName: z.string().nullable().optional(),
        expiresAt: z.iso.datetime(),
    })),
    inGracePeriod: z.boolean(),
    graceUntil: z.iso.datetime().nullable(),
    computedAt: z.iso.datetime(),
});
/**
 * Entitlement Tier 3: Observed Provisioned State (Reported from heartbeat/connector in the target cluster).
 */
export const ObservedProvisionedStateSchema = z.object({
    organisationId: z.string(),
    installationId: z.string(),
    activeModules: z.array(ModuleKeySchema),
    allocatedCapacity: z.record(CapacityMetricSchema, z.number().int().nonnegative()),
    lastReportedAt: z.iso.datetime(),
    licenseVersion: z.string().optional(),
});
/**
 * Entitlement Tier 4: Observed Usage (Actual active telemetry consumption).
 */
export const ObservedUsageSchema = z.object({
    organisationId: z.string(),
    installationId: z.string(),
    usageMetrics: z.record(CapacityMetricSchema, z.number().int().nonnegative()),
    activeDoctorsCount: z.number().int().nonnegative().optional(),
    occupiedBedsCount: z.number().int().nonnegative().optional(),
    monthlyOpdCount: z.number().int().nonnegative().optional(),
    storageUsageGb: z.number().nonnegative().optional(),
    observedAt: z.iso.datetime(),
});
/**
 * Types of Entitlement Drift.
 */
export const DRIFT_TYPES = [
    'module_unprovisioned', // Contracted/effective module missing from installation
    'module_unexpected', // Installation has module enabled without entitlement
    'capacity_over_provisioned', // Provisioned capacity exceeds entitlement
    'capacity_under_provisioned', // Provisioned capacity lower than entitlement
    'usage_overage', // Active usage exceeds capacity limit
    'grace_expired', // Grace period expired without settlement or renewal
];
export const DriftTypeSchema = z.enum(DRIFT_TYPES);
export const DRIFT_SEVERITIES = ['info', 'warning', 'critical'];
export const DriftSeveritySchema = z.enum(DRIFT_SEVERITIES);
export const DRIFT_STATUSES = ['open', 'in_reconciliation', 'resolved', 'ignored'];
export const DriftStatusSchema = z.enum(DRIFT_STATUSES);
/**
 * Reconciliation Drift Case.
 */
export const DriftCaseSchema = z.object({
    id: z.string(),
    organisationId: z.string(),
    organisationName: z.string().optional(),
    installationId: z.string().nullable().optional(),
    driftType: DriftTypeSchema,
    severity: DriftSeveritySchema,
    status: DriftStatusSchema,
    title: z.string(),
    description: z.string(),
    details: z.record(z.string(), z.unknown()),
    detectedAt: z.iso.datetime(),
    resolvedAt: z.iso.datetime().nullable().optional(),
    resolutionNote: z.string().nullable().optional(),
    resolvedByUserId: z.string().nullable().optional(),
});
/**
 * Entitlement Impact Preview Schema.
 */
export const EntitlementImpactPreviewSchema = z.object({
    organisationId: z.string(),
    proposedModules: z.array(ModuleKeySchema),
    proposedCapacity: z.record(CapacityMetricSchema, z.number().int().nonnegative()),
    modulesToAdd: z.array(ModuleKeySchema),
    modulesToRemove: z.array(ModuleKeySchema),
    capacityReductions: z.array(z.object({
        metric: CapacityMetricSchema,
        current: z.number(),
        proposed: z.number(),
        observedUsage: z.number(),
        willCauseOverage: z.boolean(),
    })),
    affectedInstallations: z.array(z.string()),
    estimatedAffectedUsers: z.number().int(),
    dataRetentionImpactDescription: z.string(),
    patientSafetyRisk: z.boolean(),
    safetyConsequences: z.string(),
    suggestedRollbackPlan: z.string(),
    highImpactRequiringStepUp: z.boolean(),
});
/**
 * Entitlement Matrix Summary (for multi-tier comparative view).
 */
export const EntitlementMatrixRowSchema = z.object({
    key: z.string(), // module name or capacity metric
    type: z.enum(['module', 'capacity']),
    label: string(),
    contracted: z.union([z.boolean(), z.number()]),
    effective: z.union([z.boolean(), z.number()]),
    provisioned: z.union([z.boolean(), z.number()]).nullable(),
    observedUsage: z.union([z.boolean(), z.number()]).nullable(),
    driftDetected: z.boolean(),
    driftDescription: z.string().nullable(),
    patientSafetyExempt: z.boolean().default(false),
});
function string() {
    return z.string();
}
//# sourceMappingURL=entitlements.js.map